import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { PlatformCard } from '@/components/PlatformCard';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { api } from '@/lib/api';
import { ShieldCheck, Zap, Headphones, Sparkles, Lock, ArrowRight, BadgeCheck, AlertTriangle, Building2, CreditCard, Rocket, FileText, Mail, MessageCircle, Facebook, Instagram, UserPlus, KeyRound, Smartphone, PlayCircle } from 'lucide-react';
import { motion } from 'framer-motion';
import { platformLogo } from '@/lib/logos';
import { HostBannerCarousel } from '@/components/HostBannerCarousel';

const HOW_IT_WORKS = [
    { Icon: CreditCard, step: '01', title: 'Pay', description: 'Choose your plan and pay securely via Razorpay — UPI, cards or net banking.' },
    { Icon: Rocket, step: '02', title: 'Instant delivery', description: 'Diamonds are credited to your in-app ID the moment payment is confirmed.' },
    { Icon: FileText, step: '03', title: 'Invoice', description: 'A GST-free invoice is generated instantly and available in your dashboard.' },
];

const HOW_TO_JOIN = [
    { Icon: UserPlus, step: '01', title: 'Sign up', description: 'Enter your email and create a password to start creating your account.' },
    { Icon: Mail, step: '02', title: 'Verify email', description: 'Enter the 6-digit OTP we send to your email inbox to confirm it.' },
    { Icon: Smartphone, step: '03', title: 'Verify WhatsApp', description: 'Confirm the OTP sent to your WhatsApp number to secure your account.' },
    { Icon: ShieldCheck, step: '04', title: 'Start recharging', description: 'Your account is ready — pick a platform and top up instantly.' },
];

const TRUST_TILES = [
    { icon: ShieldCheck, label: 'Secure', sub: 'Razorpay' },
    { icon: Zap, label: 'Instant', sub: 'Delivery' },
    { icon: Headphones, label: 'Support', sub: '7 days' },
];

const HERO_STATS = [
    { value: '2', label: 'Supported apps' },
    { value: '24×7', label: 'Instant bridge' },
    { value: '₹30+', label: 'Plans from' },
];

const FEATURE_TILES = [
    {
        Icon: Sparkles,
        title: 'Best diamond rates',
        description: 'Curated plans across our supported apps.',
    },
    {
        Icon: Lock,
        title: 'Secure checkout via Razorpay',
        description: 'UPI, cards, net banking. PCI-DSS compliant payment processing.',
    },
    {
        Icon: AlertTriangle,
        title: 'Entertainment only',
        description: 'Digital goods, non-reversible once credited. Not for gambling or money games.',
    },
];

export default function LandingPage() {
    const [platforms, setPlatforms] = useState([]);

    useEffect(() => {
        api.get('/platforms').then(r => setPlatforms(r.data.platforms || [])).catch(() => { });
    }, []);

    return (
        <PublicLayout>
            <section className="hero-bg" data-testid="landing-hero">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20 grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                    >
                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-border text-xs font-medium text-foreground/80">
                            <BadgeCheck className="h-3.5 w-3.5 text-[#1F9D8A]" /> India's reliable diamond top-up service
                        </div>
                        <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-display font-semibold tracking-tight text-foreground">
                            Top up <span className="text-[#0E2A47]">Olaparty</span> &amp; Trumeet &mdash; instantly.
                        </h1>
                        <p className="mt-4 text-base sm:text-lg text-muted-foreground max-w-xl">
                            Eagle Eyes Services is an independent recharge service for popular live-streaming apps. Secure payments via Razorpay. Instant delivery to your in-app ID.
                        </p>
                        <div className="mt-3 inline-flex items-center gap-2 text-[12px] text-foreground/70" data-testid="landing-company-name-hero">
                            <Building2 className="h-3.5 w-3.5 text-[#1F9D8A]" />
                            <span>
                                Operated by{' '}
                                <span className="font-semibold text-foreground">Shinning Eyes Entertainment Private Limited</span>
                            </span>
                        </div>
                        <div className="mt-6 flex flex-wrap gap-3">
                            <Button asChild className="bg-[#0E2A47] hover:bg-[#0B223A]" data-testid="landing-recharge-now-button">
                                <Link to="/recharge">Recharge Now <ArrowRight className="h-4 w-4 ml-1" /></Link>
                            </Button>
                            <Button variant="outline" data-testid="landing-browse-platforms-button"
                                onClick={() => document.getElementById('platforms')?.scrollIntoView({ behavior: 'smooth' })}>
                                Browse platforms
                            </Button>
                        </div>
                        <div className="mt-8 grid grid-cols-3 gap-3 max-w-md">
                            {TRUST_TILES.map((c) => (
                                <div key={c.label} className="bg-white border border-border rounded-xl p-3 flex items-center gap-2 shadow-card">
                                    <div className="h-9 w-9 rounded-lg bg-secondary flex items-center justify-center">
                                        <c.icon className="h-4 w-4 text-[#0E2A47]" />
                                    </div>
                                    <div>
                                        <div className="text-sm font-semibold">{c.label}</div>
                                        <div className="text-[11px] text-muted-foreground">{c.sub}</div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, x: 12 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.45, delay: 0.1 }}
                        className="relative"
                    >
                        <Card className="relative overflow-hidden p-7 sm:p-8 bg-gradient-to-br from-[#0E2A47] to-[#15375C] text-white border-0 shadow-xl">
                            <div className="absolute -top-12 -right-12 h-44 w-44 rounded-full bg-[#1F9D8A]/20 blur-3xl" aria-hidden />
                            <div className="absolute -bottom-16 -left-10 h-44 w-44 rounded-full bg-white/10 blur-3xl" aria-hidden />

                            <div className="relative">
                                <div className="inline-flex items-center gap-2 px-2.5 py-1 rounded-full bg-white/10 border border-white/15 text-[11px] font-medium uppercase tracking-wider">
                                    <Sparkles className="h-3 w-3" /> Live bridge active
                                </div>
                                <div className="mt-5 font-display text-3xl sm:text-4xl font-semibold leading-tight">
                                    One bridge.<br />
                                    <span className="text-[#1F9D8A]">Trusted top-ups.</span><br />
                                    Zero waiting.
                                </div>
                                <p className="mt-4 text-sm text-white/80 max-w-xs">
                                    Your in-app ID gets credited the moment Razorpay confirms your payment &mdash; signed end-to-end to the platform.
                                </p>

                                <div className="mt-7 grid grid-cols-3 gap-3">
                                    {HERO_STATS.map((s) => (
                                        <div key={s.label} className="rounded-xl bg-white/10 border border-white/15 p-3 backdrop-blur-sm">
                                            <div className="font-display text-2xl font-semibold">{s.value}</div>
                                            <div className="text-[11px] uppercase tracking-wider text-white/70 mt-0.5">{s.label}</div>
                                        </div>
                                    ))}
                                </div>

                                <div className="mt-6 flex items-center gap-2 text-[11px] text-white/70">
                                    <div className="flex -space-x-1">
                                        {platforms.slice(0, 4).map((p) => (
                                            platformLogo(p.key) ? (
                                                <img
                                                    key={p.key}
                                                    src={platformLogo(p.key)}
                                                    alt={p.name}
                                                    title={p.name}
                                                    className="h-6 w-6 rounded-full border-2 border-[#0E2A47] object-cover bg-white"
                                                />
                                            ) : (
                                                <div
                                                    key={p.key}
                                                    className="h-6 w-6 rounded-full border-2 border-[#0E2A47] flex items-center justify-center text-[10px] font-display font-semibold bg-white text-[#0E2A47]"
                                                    title={p.name}
                                                >
                                                    {p.name?.charAt(0).toUpperCase()}
                                                </div>
                                            )
                                        ))}
                                    </div>
                                    <span>{platforms.length || 2} supported apps</span>
                                </div>
                            </div>
                        </Card>
                    </motion.div>
                </div>
            </section>

            <HostBannerCarousel />

            <section id="platforms" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
                <div className="flex items-end justify-between mb-6">
                    <div>
                        <div className="text-xs uppercase tracking-wider font-medium text-[#1F9D8A]">Top-up Service</div>
                        <h2 className="font-display text-2xl sm:text-3xl font-semibold mt-1">Supported platforms</h2>
                        <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                            Tap a platform to learn more and join. Recharge happens via our secure bridge directly to your in-app ID.
                        </p>
                    </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    {platforms.map((p) => (
                        <PlatformCard key={p.key} platform={p} />
                    ))}
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-4" data-testid="landing-how-it-works">
                <div className="mb-6">
                    <div className="text-xs uppercase tracking-wider font-medium text-[#1F9D8A]">Simple &amp; transparent</div>
                    <h2 className="font-display text-2xl sm:text-3xl font-semibold mt-1">How it works</h2>
                </div>
                <div className="relative grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {HOW_IT_WORKS.map(({ Icon, step, title, description }, i) => (
                        <motion.div
                            key={title}
                            initial={{ opacity: 0, y: 12 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true }}
                            transition={{ duration: 0.4, delay: i * 0.1 }}
                            data-testid={`how-it-works-step-${i + 1}`}
                        >
                            <Card className="relative h-full p-6 overflow-hidden hover:shadow-card transition-shadow">
                                <div className="absolute top-3 right-4 font-display text-4xl font-bold text-secondary/70 select-none">{step}</div>
                                <div className="h-11 w-11 rounded-xl bg-[#0E2A47] text-white flex items-center justify-center">
                                    <Icon className="h-5 w-5" />
                                </div>
                                <div className="mt-4 font-display text-lg font-semibold">{title}</div>
                                <div className="text-sm text-muted-foreground mt-1">{description}</div>
                            </Card>
                        </motion.div>
                    ))}
                </div>
            </section>

            <section id="how-to-join" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14" data-testid="landing-how-to-join">
                <div className="mb-6">
                    <div className="text-xs uppercase tracking-wider font-medium text-[#1F9D8A]">Getting started</div>
                    <h2 className="font-display text-2xl sm:text-3xl font-semibold mt-1">How to create your account</h2>
                    <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                        New here? Watch this quick guide, then follow the steps to join in under a minute — it's free.
                    </p>
                </div>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-start">
                    <motion.div
                        initial={{ opacity: 0, y: 12 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.4 }}
                        className="relative"
                    >
                        <Card className="overflow-hidden p-2 bg-[#0E2A47] border-0 shadow-xl">
                            <div className="inline-flex items-center gap-2 px-3 py-2 text-[11px] font-medium uppercase tracking-wider text-white/80">
                                <PlayCircle className="h-4 w-4 text-[#1F9D8A]" /> Account creation walkthrough
                            </div>
                            <video
                                src="/account-guide.mp4"
                                controls
                                playsInline
                                preload="metadata"
                                className="w-full rounded-xl bg-black aspect-video object-contain"
                                data-testid="how-to-join-video"
                            >
                                Sorry, your browser doesn't support embedded videos.
                            </video>
                        </Card>
                    </motion.div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        {HOW_TO_JOIN.map(({ Icon, step, title, description }, i) => (
                            <motion.div
                                key={title}
                                initial={{ opacity: 0, y: 12 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ duration: 0.4, delay: i * 0.08 }}
                                data-testid={`how-to-join-step-${i + 1}`}
                            >
                                <Card className="relative h-full p-5 overflow-hidden hover:shadow-card transition-shadow">
                                    <div className="absolute top-2 right-3 font-display text-3xl font-bold text-secondary/70 select-none">{step}</div>
                                    <div className="h-10 w-10 rounded-xl bg-[#1F9D8A] text-white flex items-center justify-center">
                                        <Icon className="h-5 w-5" />
                                    </div>
                                    <div className="mt-3 font-display text-base font-semibold">{title}</div>
                                    <div className="text-sm text-muted-foreground mt-1">{description}</div>
                                </Card>
                            </motion.div>
                        ))}
                        <div className="sm:col-span-2">
                            <Button asChild className="bg-[#0E2A47] hover:bg-[#0B223A]" data-testid="how-to-join-signup-button">
                                <Link to="/register">Create your account <ArrowRight className="h-4 w-4 ml-1" /></Link>
                            </Button>
                        </div>
                    </div>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {FEATURE_TILES.map(({ Icon, title, description }) => (
                        <Card key={title} className="p-5">
                            <Icon className={`h-5 w-5 ${title.includes('Entertainment') ? 'text-[#B45309]' : 'text-[#1F9D8A]'}`} />
                            <div className="mt-2 font-display font-semibold">{title}</div>
                            <div className="text-sm text-muted-foreground mt-1">{description}</div>
                        </Card>
                    ))}
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-14" data-testid="landing-company-info">
                <Card className="p-6 sm:p-8 bg-gradient-to-br from-secondary/40 to-white border-border">
                    <div className="flex flex-col sm:flex-row gap-4 sm:items-center sm:justify-between">
                        <div className="flex items-start gap-3">
                            <div className="h-12 w-12 rounded-xl bg-[#0E2A47] text-white flex items-center justify-center shrink-0">
                                <Building2 className="h-6 w-6" />
                            </div>
                            <div>
                                <div className="text-xs uppercase tracking-wider text-[#1F9D8A] font-medium">An initiative by</div>
                                <div className="font-display text-xl sm:text-2xl font-semibold mt-0.5">
                                    Shinning Eyes Entertainment Private Limited
                                </div>
                                <div className="text-sm text-muted-foreground mt-1">
                                    Eagle Eyes Services is a brand of Shinning Eyes Entertainment Pvt Ltd &mdash; building India's most reliable digital top-up infrastructure for live-streaming creators.
                                </div>
                            </div>
                        </div>
                        <Button asChild variant="outline" className="self-start sm:self-center shrink-0">
                            <Link to="/policies/terms">Terms &amp; Compliance</Link>
                        </Button>
                    </div>
                </Card>
            </section>

            <section id="contact" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16" data-testid="landing-contact-section">
                <div className="text-center max-w-2xl mx-auto">
                    <div className="text-xs uppercase tracking-wider text-[#1F9D8A] font-medium">Contact us</div>
                    <h2 className="font-display text-2xl sm:text-3xl font-semibold mt-1">We're here to help</h2>
                    <p className="text-sm text-muted-foreground mt-2">Questions about a recharge, an invoice, or becoming a reseller? Reach out on any channel below — our support team typically replies within a few hours.</p>
                </div>
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                    <a href="mailto:support@shinningeyes.com" data-testid="contact-email-link" className="group">
                        <Card className="p-5 h-full hover:shadow-card transition-shadow">
                            <div className="h-11 w-11 rounded-xl bg-[#0E2A47] text-white flex items-center justify-center"><Mail className="h-5 w-5" /></div>
                            <div className="mt-3 font-display font-semibold">Email support</div>
                            <div className="text-sm text-[#1F9D8A] mt-1 break-all group-hover:underline">support@shinningeyes.com</div>
                            <div className="text-xs text-muted-foreground mt-1">For invoices, refunds &amp; queries</div>
                        </Card>
                    </a>
                    <a href="https://wa.me/917898586002?text=Hi%20Eagle%20Eyes%20Services%2C%20I%20need%20help%20with%20a%20recharge." target="_blank" rel="noopener noreferrer" data-testid="contact-whatsapp-link" className="group">
                        <Card className="p-5 h-full hover:shadow-card transition-shadow">
                            <div className="h-11 w-11 rounded-xl bg-[#25D366] text-white flex items-center justify-center"><MessageCircle className="h-5 w-5" /></div>
                            <div className="mt-3 font-display font-semibold">WhatsApp chat</div>
                            <div className="text-sm text-[#1F9D8A] mt-1 group-hover:underline">+91 78985 86002</div>
                            <div className="text-xs text-muted-foreground mt-1">Fastest response — tap to chat</div>
                        </Card>
                    </a>
                    <a href="https://www.facebook.com/shinningeyesent" target="_blank" rel="noopener noreferrer" data-testid="contact-facebook-link" className="group">
                        <Card className="p-5 h-full hover:shadow-card transition-shadow">
                            <div className="h-11 w-11 rounded-xl bg-[#1877F2] text-white flex items-center justify-center"><Facebook className="h-5 w-5" /></div>
                            <div className="mt-3 font-display font-semibold">Facebook</div>
                            <div className="text-sm text-[#1F9D8A] mt-1 group-hover:underline">@shinningeyesent</div>
                            <div className="text-xs text-muted-foreground mt-1">Updates &amp; announcements</div>
                        </Card>
                    </a>
                    <a href="https://www.instagram.com/eagle.streaming" target="_blank" rel="noopener noreferrer" data-testid="contact-instagram-link" className="group">
                        <Card className="p-5 h-full hover:shadow-card transition-shadow">
                            <div className="h-11 w-11 rounded-xl bg-gradient-to-br from-[#F58529] via-[#DD2A7B] to-[#8134AF] text-white flex items-center justify-center"><Instagram className="h-5 w-5" /></div>
                            <div className="mt-3 font-display font-semibold">Instagram</div>
                            <div className="text-sm text-[#1F9D8A] mt-1 group-hover:underline">@eagle.streaming</div>
                            <div className="text-xs text-muted-foreground mt-1">Behind the scenes &amp; offers</div>
                        </Card>
                    </a>
                </div>
                <div className="mt-6 flex justify-center">
                    <Button asChild className="bg-[#25D366] hover:bg-[#1EBE5B] text-white" data-testid="contact-whatsapp-button">
                        <a href="https://wa.me/917898586002?text=Hi%20Eagle%20Eyes%20Services%2C%20I%20need%20help%20with%20a%20recharge." target="_blank" rel="noopener noreferrer">
                            <MessageCircle className="h-4 w-4 mr-2" /> Message us on WhatsApp
                        </a>
                    </Button>
                </div>
            </section>
        </PublicLayout>
    );
}
